import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../clients/apiClient';

export interface PendingFollowUp {
  id: string;
  recruiter_name: string;
  recruiter_title: string;
  company: string;
  domain: string;
  role: string;
  sent_at: string | null;
  follow_up_number: number;
}

async function fetchPendingFollowUps(): Promise<PendingFollowUp[]> {
  const { data } = await apiClient.get<PendingFollowUp[]>('/api/follow-ups/pending');
  return data;
}

export interface FollowUpPreview {
  outreach_id: string;
  subject: string;
  body: string;
  recruiter_name: string;
  role: string;
  company: string;
  follow_up_number: number;
}

export async function fetchFollowUpPreview(outreachId: string): Promise<FollowUpPreview> {
  const { data } = await apiClient.get<FollowUpPreview>(`/api/follow-ups/${outreachId}/preview`);
  return data;
}

export function useFollowUpQueue() {
  const queryClient = useQueryClient();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [loadingCompany, setLoadingCompany] = useState<string | null>(null);

  const query = useQuery<PendingFollowUp[]>({
    queryKey: ['follow-ups-pending'],
    queryFn: fetchPendingFollowUps,
    refetchInterval: 30_000,
  });

  const getPreview = async (outreachId: string): Promise<FollowUpPreview> => {
    return fetchFollowUpPreview(outreachId);
  };

  const approve = async (outreachId: string) => {
    setLoadingId(outreachId);
    try {
      await apiClient.post(`/api/follow-ups/${outreachId}/approve`);
      queryClient.setQueryData<PendingFollowUp[]>(['follow-ups-pending'], (prev) =>
        (prev ?? []).filter((o) => o.id !== outreachId)
      );
    } finally {
      setLoadingId(null);
    }
  };

  const skip = async (outreachId: string) => {
    setLoadingId(outreachId);
    try {
      await apiClient.post(`/api/follow-ups/${outreachId}/skip`);
      queryClient.setQueryData<PendingFollowUp[]>(['follow-ups-pending'], (prev) =>
        (prev ?? []).filter((o) => o.id !== outreachId)
      );
    } finally {
      setLoadingId(null);
    }
  };

  const skipAll = async (company: string, outreachIds: string[]) => {
    setLoadingCompany(company);
    try {
      await Promise.all(outreachIds.map((id) => apiClient.post(`/api/follow-ups/${id}/skip`)));
      queryClient.setQueryData<PendingFollowUp[]>(['follow-ups-pending'], (prev) =>
        (prev ?? []).filter((o) => !outreachIds.includes(o.id))
      );
    } finally {
      setLoadingCompany(null);
    }
  };

  return { ...query, approve, skip, skipAll, getPreview, loadingId, loadingCompany };
}
